// Iter 291 — Error Boundary
// --------------------------------------------------------------
// The Order Register table renders hundreds of rows with dozens of
// chips per row; any single unexpected field shape used to unmount the
// whole React tree → the team saw a BLANK WHITE PAGE mid-work and had
// to reload (losing their place). This boundary converts that crash
// into a recoverable panel and auto-retries the tab after a reload.
// --------------------------------------------------------------
import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep a console trail so support can identify the exact field.
    console.error("[ErrorBoundary]", this.props.name || "panel", error, info);
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <div className="m-6 rounded-xl border border-rose-200 bg-rose-50 p-6 text-center"
             data-testid="error-boundary">
          <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
          <div className="text-sm font-semibold text-rose-800">
            This panel hit an unexpected error
          </div>
          <div className="text-xs text-rose-600 mt-1 max-w-lg mx-auto break-words">
            {String(this.state.error?.message || this.state.error)}
          </div>
          <div className="text-[11px] text-rose-500 mt-2">
            Your data is safe — this is a display issue only.
          </div>
          <div className="mt-3 flex items-center justify-center gap-2">
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700"
              data-testid="error-boundary-retry"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-rose-300 text-rose-700 text-xs font-semibold hover:bg-rose-100"
              data-testid="error-boundary-reload"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
